import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { TemplateFolderSelectModal } from "@/pages/talent/templates/TemplateFolderSelectModal.tsx";
import { TemplateFoldersView } from "@/pages/talent/templates/TemplateFoldersView.tsx";
import { useTemplateFolders } from "@/queries/forms/template-folder.query.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { PerformanceReviewTemplatesTable } from "@/pages/talent/performance-reviews/templates/components/PerformanceReviewTemplatesTable.tsx";
import {
  PerformanceReviewTemplateEditPageStep,
  useTemplateEditPage as useReviewTemplateEditPage,
} from "@/pages/talent/performance-reviews/templates/edit/performance-review-template-edit.tsx";
import { SurveyTemplatesTable } from "@/pages/talent/surveys/templates/components/SurveyTemplatesTable.tsx";
import { useTemplateEditPage as useSurveyTemplateEditPage } from "@/pages/talent/surveys/templates/edit/pages/survey-template-edit-pages.tsx";
import { SurveyTemplateEditPageStep } from "@/pages/talent/surveys/templates/edit/survey-template-edit.ts";
import { usePerformanceReviewTemplateSave } from "@/queries/performance-reviews/use-performance-review-template.query.ts";
import { useSurveyTemplateSave } from "@/queries/surveys/use-survey-templates.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { TemplatesSections } from "@/pages/talent/templates/templates-sections.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { Button, FontAwesomeIcon, Page, useModals } from "@skailer-com/uikit";
import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";

const TemplatesLayout = ({
  title,
  createLabel,
  canCreate,
  onCreate,
  children,
  navigateTo,
}: {
  navigateTo?: string;
  title: string;
  createLabel: string;
  canCreate: boolean;
  onCreate: () => Promise<void>;
  children: ReactNode;
}) => {
  useTitle(title);

  return (
    <Page className="h-full">
      <Page.Header title={title} showBack={!!navigateTo} navigateTo={navigateTo}>
        {canCreate && (
          <Button
            onClick={() => {
              void onCreate();
            }}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {createLabel}
          </Button>
        )}
      </Page.Header>
      <Page.Content stretch>{children}</Page.Content>
    </Page>
  );
};

export const TemplatesIndexRedirect = () => {
  const { canIView, canIEdit, isFeatureEnabled } = usePermissions();

  const canViewSurveyTemplates =
    isFeatureEnabled(ApiFeatureType.talent_surveys) &&
    canIEdit(ApiPermission.surveys_module) &&
    canIView(ApiPermission.survey_templates);

  if (canViewSurveyTemplates) {
    return <Navigate to={`/templates/${TemplatesSections.surveys}`} replace />;
  }

  return <Navigate to={`/templates/${TemplatesSections.performanceReviews}`} replace />;
};

export const SurveyTemplatesIndex = ({ list = false }: { list?: boolean }) => {
  const { folderId } = useParams<{ folderId: string }>();
  const { data: folders, isLoading: foldersLoading } = useTemplateFolders("surveys");
  const selectedFolder = folders?.find((folder) => folder.id === Number(folderId));
  const canShowList = !folderId || selectedFolder !== undefined;
  const { t } = useTranslation();
  const { navigateToStep } = useSurveyTemplateEditPage();
  const surveyTemplateSave = useSurveyTemplateSave();
  const { canIView, canIEdit, isFeatureEnabled } = usePermissions();

  const canEditSurveyTemplates =
    isFeatureEnabled(ApiFeatureType.talent_surveys) &&
    canIEdit(ApiPermission.surveys_module) &&
    canIView(ApiPermission.survey_templates) &&
    canIEdit(ApiPermission.survey_templates);

  const { openModal } = useModals();
  const createTemplate = useCallback(
    async (destinationFolderId: number) => {
      const template = await surveyTemplateSave.mutateAsync({
        id: undefined,
        payload: { title: "", folder_id: destinationFolderId },
      });

      analyticsService.trackEvent(analyticEvents.surveyTemplates.created, {
        [analyticProperties.id]: template.id,
      });

      navigateToStep(template.id, SurveyTemplateEditPageStep.details);
    },
    [navigateToStep, surveyTemplateSave]
  );

  const handleCreate = useCallback(async () => {
    if (!selectedFolder) {
      openModal(TemplateFolderSelectModal, {
        module: "surveys",
        modalTitle: t("surveys:create_survey_template"),
        acceptTitle: t("surveys:create_survey_template"),
        onConfirm: createTemplate,
      });
      return;
    }

    try {
      await createTemplate(selectedFolder.id);
    } catch {
      return;
    }
  }, [createTemplate, openModal, selectedFolder, t]);

  return (
    <TemplatesLayout
      title={
        list
          ? folderId
            ? (selectedFolder?.title ?? (foldersLoading ? "" : t("common:nothing_found")))
            : t("common:all_templates")
          : t("common:templates")
      }
      navigateTo={list ? "/templates/surveys" : undefined}
      createLabel={t("surveys:create_survey_template")}
      canCreate={canEditSurveyTemplates && (!list || canShowList)}
      onCreate={handleCreate}
    >
      {list ? (
        canShowList ? (
          <SurveyTemplatesTable key={folderId ?? "all"} folderId={selectedFolder?.id} />
        ) : (
          !foldersLoading && <EmptyStateNothingFound />
        )
      ) : (
        <TemplateFoldersView module="surveys" canManage={canEditSurveyTemplates} />
      )}
    </TemplatesLayout>
  );
};

export const PerformanceReviewTemplatesIndex = ({ list = false }: { list?: boolean }) => {
  const { folderId } = useParams<{ folderId: string }>();
  const { data: folders, isLoading: foldersLoading } = useTemplateFolders("performance-reviews");
  const selectedFolder = folders?.find((folder) => folder.id === Number(folderId));
  const canShowList = !folderId || selectedFolder !== undefined;
  const { t } = useTranslation();
  const { navigateToStep } = useReviewTemplateEditPage();
  const reviewTemplateSave = usePerformanceReviewTemplateSave();
  const { canIView, canIEdit, isFeatureEnabled } = usePermissions();

  const canEditReviewTemplates =
    isFeatureEnabled(ApiFeatureType.talent_performance_reviews) &&
    canIEdit(ApiPermission.performance_reviews_module) &&
    canIView(ApiPermission.performance_review_templates) &&
    canIEdit(ApiPermission.performance_review_templates);

  const { openModal } = useModals();
  const createTemplate = useCallback(
    async (destinationFolderId: number) => {
      const template = await reviewTemplateSave.mutateAsync({
        id: undefined,
        payload: { title: "", folder_id: destinationFolderId },
      });

      analyticsService.trackEvent(analyticEvents.performanceReviewsTemplates.created, {
        [analyticProperties.id]: template.id,
      });

      navigateToStep(template.id, PerformanceReviewTemplateEditPageStep.details);
    },
    [navigateToStep, reviewTemplateSave]
  );

  const handleCreate = useCallback(async () => {
    if (!selectedFolder) {
      openModal(TemplateFolderSelectModal, {
        module: "performance-reviews",
        modalTitle: t("performance_review:create_review_template"),
        acceptTitle: t("performance_review:create_review_template"),
        onConfirm: createTemplate,
      });
      return;
    }

    try {
      await createTemplate(selectedFolder.id);
    } catch {
      return;
    }
  }, [createTemplate, openModal, selectedFolder, t]);

  return (
    <TemplatesLayout
      title={
        list
          ? folderId
            ? (selectedFolder?.title ?? (foldersLoading ? "" : t("common:nothing_found")))
            : t("common:all_templates")
          : t("performance_review:performance_review_templates")
      }
      navigateTo={list ? "/templates/performance-reviews" : undefined}
      createLabel={t("performance_review:create_review_template")}
      canCreate={canEditReviewTemplates && (!list || canShowList)}
      onCreate={handleCreate}
    >
      {list ? (
        canShowList ? (
          <PerformanceReviewTemplatesTable key={folderId ?? "all"} folderId={selectedFolder?.id} />
        ) : (
          !foldersLoading && <EmptyStateNothingFound />
        )
      ) : (
        <TemplateFoldersView module="performance-reviews" canManage={canEditReviewTemplates} />
      )}
    </TemplatesLayout>
  );
};
